import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { EvChipItem, EvComboboxOption } from '@evinced-private/ui-common';

import { useGetTenantUsers } from 'src/hooks/queries/useGetTenantUsers';
import { getActiveTenantUsers } from 'src/services/TenantsService';

import { SchedulingSectionContext } from './context';
import {
	DEFAULT_PAGE_SIZE,
	RECIPIENT_LOOKUP_PAGE_SIZE,
	SEARCH_PAGE_SIZE
} from './EmailSettings.constants';
import { getUserLabel, mapUsersToOptions } from './EmailSettings.utils';

interface UseEmailRecipientsReturn {
	recipients: EvChipItem[];
	initialOptions: EvComboboxOption[];
	loadUserOptions: (searchText: string, cb: (options: EvComboboxOption[]) => void) => void;
	handleRecipientSelect: (selected: EvComboboxOption[]) => void;
	handleRemoveRecipient: (chipId: string) => void;
}

const RECIPIENT_LABEL_CACHE_KEY = 'email-recipient-labels-by-user-id';
const recipientLabelCache = new Map<string, string>();
let hasLoadedStoredRecipientLabels = false;

const loadStoredRecipientLabels = (): void => {
	if (hasLoadedStoredRecipientLabels) {
		return;
	}

	hasLoadedStoredRecipientLabels = true;
	try {
		const storedLabels = JSON.parse(sessionStorage.getItem(RECIPIENT_LABEL_CACHE_KEY) || '{}');
		Object.entries(storedLabels).forEach(([userId, label]) => {
			if (typeof label === 'string') {
				recipientLabelCache.set(userId, label);
			}
		});
	} catch {
		recipientLabelCache.clear();
	}
};

const persistRecipientLabels = (): void => {
	try {
		sessionStorage.setItem(
			RECIPIENT_LABEL_CACHE_KEY,
			JSON.stringify(Object.fromEntries(recipientLabelCache))
		);
	} catch {
		// Storage can be unavailable in private or restricted browsing modes.
	}
};

const getCachedRecipientLabel = (userId: string): string | undefined => {
	loadStoredRecipientLabels();
	return recipientLabelCache.get(userId);
};

const cacheRecipientLabel = (userId: string, label: string): void => {
	loadStoredRecipientLabels();
	recipientLabelCache.set(userId, label);
	persistRecipientLabels();
};

export const clearEmailRecipientLabelCacheForTests = (): void => {
	recipientLabelCache.clear();
	hasLoadedStoredRecipientLabels = false;
	try {
		sessionStorage.removeItem(RECIPIENT_LABEL_CACHE_KEY);
	} catch {
		// Storage can be unavailable in private or restricted browsing modes.
	}
};

export const useEmailRecipients = (): UseEmailRecipientsReturn => {
	const { config, updateConfig } = useContext(SchedulingSectionContext);
	const { data: initialUsers } = useGetTenantUsers(undefined, RECIPIENT_LOOKUP_PAGE_SIZE);
	const [cachedLabelById] = useState(() => {
		loadStoredRecipientLabels();
		return new Map(recipientLabelCache);
	});
	const userLabelById = useMemo(() => {
		const labels = new Map(cachedLabelById);
		(initialUsers || []).forEach((user) => {
			labels.set(user.id, getUserLabel(user));
		});
		return labels;
	}, [cachedLabelById, initialUsers]);
	const [recipients, setRecipients] = useState<EvChipItem[]>(() =>
		(config.emailRecipientUserIds || []).map((userId) => ({
			id: userId,
			label: userLabelById.get(userId) || getCachedRecipientLabel(userId) || userId
		}))
	);

	useEffect(() => {
		if (!initialUsers?.length) {
			return;
		}

		initialUsers.forEach((user) => {
			recipientLabelCache.set(user.id, getUserLabel(user));
		});
		persistRecipientLabels();
	}, [initialUsers]);

	useEffect(() => {
		const recipientIds = config.emailRecipientUserIds || [];
		loadStoredRecipientLabels();
		const cachedRecipientLabelById = new Map(recipientLabelCache);
		setRecipients((prevRecipients) => {
			const previousRecipientById = new Map(
				prevRecipients.map((recipient) => [recipient.id, recipient])
			);
			const nextRecipients = recipientIds.map((userId) => ({
				id: userId,
				label:
					userLabelById.get(userId) ||
					cachedRecipientLabelById.get(userId) ||
					previousRecipientById.get(userId)?.label ||
					userId
			}));
			const unchanged =
				nextRecipients.length === prevRecipients.length &&
				nextRecipients.every(
					(recipient, index) =>
						recipient.id === prevRecipients[index]?.id &&
						recipient.label === prevRecipients[index]?.label
				);

			return unchanged ? prevRecipients : nextRecipients;
		});
	}, [config.emailRecipientUserIds, userLabelById]);

	const disabledIds = useMemo(() => new Set(recipients.map((r) => r.id)), [recipients]);

	const initialOptions = useMemo(
		() => (initialUsers ? mapUsersToOptions(initialUsers, disabledIds) : []),
		[initialUsers, disabledIds]
	);

	const recipientsWithResolvedLabels = useMemo(
		() =>
			recipients.map((recipient) => ({
				...recipient,
				label: userLabelById.get(recipient.id) || recipient.label
			})),
		[recipients, userLabelById]
	);

	const loadUserOptions = useCallback(
		(searchText: string, cb: (options: EvComboboxOption[]) => void): void => {
			const pageSize = searchText ? SEARCH_PAGE_SIZE : DEFAULT_PAGE_SIZE;
			getActiveTenantUsers(searchText, pageSize).then((users) => {
				cb(mapUsersToOptions(users, disabledIds));
			});
		},
		[disabledIds]
	);

	const handleRecipientSelect = useCallback(
		(selected: EvComboboxOption[]): void => {
			if (!selected?.[0]) {
				return;
			}
			const option = selected[0];
			const userId = String(option.value);
			if (recipients.some((r) => r.id === userId)) {
				return;
			}

			cacheRecipientLabel(userId, option.label);
			const nextRecipients = [...recipients, { id: userId, label: option.label }];
			setRecipients(nextRecipients);
			updateConfig({ emailRecipientUserIds: nextRecipients.map((r) => r.id) });
		},
		[recipients, updateConfig]
	);

	const handleRemoveRecipient = useCallback(
		(chipId: string): void => {
			const nextRecipients = recipients.filter((r) => r.id !== chipId);
			if (nextRecipients.length === recipients.length) {
				return;
			}

			setRecipients(nextRecipients);
			updateConfig({ emailRecipientUserIds: nextRecipients.map((r) => r.id) });
		},
		[recipients, updateConfig]
	);

	return {
		recipients: recipientsWithResolvedLabels,
		initialOptions,
		loadUserOptions,
		handleRecipientSelect,
		handleRemoveRecipient
	};
};
